export const DOCUMENT_SELECTION_EXPERIMENTS = Object.freeze({
    PERSIST_ACROSS_FOLDERS: 'persist_across_folders',
    DRAG_SELECTED_ITEMS: 'drag_selected_items',
});

export const DOCUMENT_SELECTION_PREFERENCE_EVENT = 'casedb:document-selection-preference';
export const DOCUMENT_SELECTION_MOVE_EVENT = 'casedb:document-selection-moved';

const STORAGE_KEYS = Object.freeze({
    [DOCUMENT_SELECTION_EXPERIMENTS.PERSIST_ACROSS_FOLDERS]: 'casedb:documents:persist-selection',
    [DOCUMENT_SELECTION_EXPERIMENTS.DRAG_SELECTED_ITEMS]: 'casedb:documents:drag-selected-items',
});

const getStorage = () => {
    try {
        return typeof window !== 'undefined' ? window.localStorage : null;
    } catch (_) {
        return null;
    }
};

export const getDocumentSelectionPreference = (preference) => {
    const storageKey = STORAGE_KEYS[preference];
    if (!storageKey) return false;

    try {
        return getStorage()?.getItem(storageKey) === '1';
    } catch (_) {
        return false;
    }
};

export const setDocumentSelectionPreference = (preference, enabled) => {
    const storageKey = STORAGE_KEYS[preference];
    if (!storageKey) return false;

    const nextEnabled = Boolean(enabled);
    try {
        getStorage()?.setItem(storageKey, nextEnabled ? '1' : '0');
    } catch (_) {
        return false;
    }

    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(DOCUMENT_SELECTION_PREFERENCE_EVENT, {
            detail: { preference, enabled: nextEnabled },
        }));
    }

    return nextEnabled;
};

export const getDocumentSelectionPreferences = () => ({
    persistAcrossFolders: getDocumentSelectionPreference(
        DOCUMENT_SELECTION_EXPERIMENTS.PERSIST_ACROSS_FOLDERS
    ),
    dragSelectedItems: getDocumentSelectionPreference(
        DOCUMENT_SELECTION_EXPERIMENTS.DRAG_SELECTED_ITEMS
    ),
});

const uniqueKeys = (keys) => Array.from(new Set(
    (Array.isArray(keys) ? keys : [])
        .filter(key => typeof key === 'string')
        .filter(key => key.length > 0)
));

const getStorageScope = (key) => {
    const match = String(key || '').match(/^(cases|leads)\/([^/]+)\//i);
    return match ? `${match[1].toLowerCase()}/${match[2]}` : '';
};

export const collapseNestedDocumentSelection = (keys) => {
    const candidates = uniqueKeys(keys);
    const selectedFolders = candidates.filter(key => key.endsWith('/'));

    return candidates.filter(key => !selectedFolders.some(folderKey => (
        folderKey !== key && key.startsWith(folderKey)
    )));
};

export const getDocumentDragSourceKeys = ({
    draggedKey,
    selectedKeys,
    dragSelectedItems = false,
}) => {
    const normalizedDraggedKey = typeof draggedKey === 'string' ? draggedKey : '';
    if (!normalizedDraggedKey) return [];

    const selection = uniqueKeys(selectedKeys);
    if (!dragSelectedItems || !selection.includes(normalizedDraggedKey)) {
        return [normalizedDraggedKey];
    }

    const draggedScope = getStorageScope(normalizedDraggedKey);
    const sameScopeSelection = draggedScope
        ? selection.filter(key => getStorageScope(key) === draggedScope)
        : selection.filter(key => key === normalizedDraggedKey);

    return collapseNestedDocumentSelection(sameScopeSelection);
};

export const buildDocumentDragPayload = ({
    type,
    item,
    sourceFolder,
    selectedKeys,
    dragSelectedItems = false,
}) => {
    const draggedKey = item?.key || item?.file_path || item?.path || '';
    const sourceKeys = getDocumentDragSourceKeys({
        draggedKey,
        selectedKeys,
        dragSelectedItems,
    });
    const selectedGroupDrag = dragSelectedItems
        && uniqueKeys(selectedKeys).includes(String(draggedKey || ''));

    return {
        type,
        item,
        sourceFolder,
        ...(selectedGroupDrag && (sourceKeys.length > 1 || sourceKeys[0] !== draggedKey) ? {
            documentDragVersion: 2,
            sourceKeys,
        } : {}),
    };
};

export const resolveDocumentDropSourceKeys = (dragData) => {
    const draggedKey = dragData?.item?.key || dragData?.item?.file_path || dragData?.item?.path || '';
    const requestedKeys = dragData?.documentDragVersion === 2 && Array.isArray(dragData?.sourceKeys)
        ? dragData.sourceKeys
        : [];

    if (!draggedKey) return [];
    const draggedKeyCovered = requestedKeys.some(key => (
        key === draggedKey || (String(key).endsWith('/') && draggedKey.startsWith(key))
    ));
    if (!draggedKeyCovered) return [draggedKey];

    const draggedScope = getStorageScope(draggedKey);
    const safeKeys = draggedScope
        ? requestedKeys.filter(key => getStorageScope(key) === draggedScope)
        : [draggedKey];

    return collapseNestedDocumentSelection(safeKeys);
};

export const removeMovedDocumentSelectionKeys = (selectedKeys, movedKeys) => {
    const movedRoots = uniqueKeys(movedKeys);
    if (movedRoots.length === 0) return uniqueKeys(selectedKeys);

    return uniqueKeys(selectedKeys).filter(selectedKey => !movedRoots.some(movedKey => (
        selectedKey === movedKey
        || (movedKey.endsWith('/') && selectedKey.startsWith(movedKey))
    )));
};

export const toggleVisibleDocumentSelection = ({
    selectedKeys,
    visibleKeys,
    persistAcrossFolders = false,
    persistAcrossPages = false,
}) => {
    const currentSelection = uniqueKeys(selectedKeys);
    const currentVisibleKeys = uniqueKeys(visibleKeys);
    const allVisibleSelected = currentVisibleKeys.length > 0
        && currentVisibleKeys.every(key => currentSelection.includes(key));

    if (!persistAcrossFolders && !persistAcrossPages) {
        return allVisibleSelected ? [] : currentVisibleKeys;
    }

    if (allVisibleSelected) {
        const visibleSet = new Set(currentVisibleKeys);
        return currentSelection.filter(key => !visibleSet.has(key));
    }

    return uniqueKeys([...currentSelection, ...currentVisibleKeys]);
};

const caseRelativeDocumentKey = (key) => {
    const normalizedKey = String(key || '').trim().replace(/^\/+/, '');
    const caseNameMarker = '/{{Name}}/';
    const markerIndex = normalizedKey.indexOf(caseNameMarker);
    if (markerIndex >= 0) {
        return normalizedKey.slice(markerIndex + caseNameMarker.length);
    }

    return normalizedKey.replace(/^(?:cases|leads)\/[^/]+\/?/i, '');
};

const caseRelativeFolderPath = (folderName) => String(folderName || '')
    .trim()
    .replace(/^\/+|\/+$/g, '')
    .replace(/^\{\{Name\}\}(?:\/|$)/, '')
    .replace(/^root$/i, '');

export const buildDocumentSelectionDisplayRows = (selectedKeys, folderName) => {
    const currentFolderPath = caseRelativeFolderPath(folderName);

    return uniqueKeys(selectedKeys).map((key) => {
        const isFolder = key.endsWith('/');
        const relativePath = caseRelativeDocumentKey(key).replace(/\/+$/g, '');
        const pathParts = relativePath.split('/').filter(Boolean);
        const name = pathParts.pop() || key;
        const parentPath = pathParts.join('/');

        return {
            key,
            name,
            parentPath,
            displayPath: [parentPath, name].filter(Boolean).join('/'),
            isFolder,
            isCurrentFolder: parentPath === currentFolderPath,
        };
    });
};

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

export const getDocumentSelectionPopoverLayout = ({
    triggerRect,
    popoverWidth = 440,
    popoverHeight = 390,
    viewportWidth,
    viewportHeight,
    margin = 12,
    gap = 8,
}) => {
    const width = Math.max(0, Math.min(
        Number(popoverWidth) || 0,
        Math.max(0, (Number(viewportWidth) || 0) - (margin * 2))
    ));
    const left = clamp(
        Number(triggerRect?.left) || 0,
        margin,
        Math.max(margin, (Number(viewportWidth) || 0) - width - margin)
    );
    const spaceBelow = Math.max(
        0,
        (Number(viewportHeight) || 0) - (Number(triggerRect?.bottom) || 0) - gap - margin
    );
    const spaceAbove = Math.max(0, (Number(triggerRect?.top) || 0) - gap - margin);
    const showAbove = spaceBelow < Math.min(Number(popoverHeight) || 0, 240)
        && spaceAbove > spaceBelow;
    const availableHeight = showAbove ? spaceAbove : spaceBelow;

    return {
        left,
        width,
        maxHeight: Math.min(Number(popoverHeight) || 0, availableHeight),
        placement: showAbove ? 'above' : 'below',
        ...(showAbove
            ? {
                top: 'auto',
                bottom: Math.max(margin, (Number(viewportHeight) || 0) - (Number(triggerRect?.top) || 0) + gap),
            }
            : {
                top: Math.max(margin, (Number(triggerRect?.bottom) || 0) + gap),
                bottom: 'auto',
            }),
    };
};
